/**
 * Pull progress for every macro activity that has linked discipline activities,
 * in one action. Shows what moved rather than a bare "done".
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, RefreshCw, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const pct = (v) => (v == null ? '—' : `${Math.round(v * 10) / 10}%`);

export function PullAllButton({ scheduleId, linkedCount = 0, onDone }) {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/macro-links/schedules/${scheduleId}/pull-all`, {});
      setResult(data);
      toast.success(`${data.pulled} of ${data.total} linked activities updated`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pull failed');
    } finally { setRunning(false); }
  };

  const moved = (result?.results || []).filter(r => r.after != null && Math.abs(r.after - r.before) > 0.05);

  return (
    <>
      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-sky-400 hover:text-sky-300"
              onClick={() => { setResult(null); setOpen(true); }}
              title="Pull progress from the linked Engineering / Procurement / F&C / Pre-comm schedules for every activity that has links"
              data-testid="pull-all-toolbar-btn">
        <RefreshCw className="w-3.5 h-3.5 mr-1" />Pull All Links
        {linkedCount > 0 && <span className="ml-1 opacity-60">({linkedCount})</span>}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="pull-all-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Pull progress for every linked activity</DialogTitle>
          </DialogHeader>
          {!result ? (
            <p className="text-xs text-muted-foreground">
              This recomputes {linkedCount || 'every'} linked macro activit{linkedCount === 1 ? 'y' : 'ies'} from
              their Engineering, Procurement, Fabrication &amp; Construction and Pre-commissioning activities,
              using each activity's discipline weights, and writes the result onto the macro schedule.
              Activities with no links are left alone.
            </p>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-foreground">
                <strong>{result.pulled}</strong> of {result.total} activities updated · {moved.length} changed value
              </p>
              <ScrollArea className="max-h-72 border border-border rounded">
                <table className="w-full text-[11px]" data-testid="pull-all-results">
                  <thead className="text-muted-foreground uppercase text-[9px] sticky top-0 bg-background">
                    <tr>{['Activity', 'Before', '', 'After'].map((h, i) => (
                      <th key={i} className="text-left px-2 py-1 font-medium">{h}</th>))}</tr>
                  </thead>
                  <tbody>
                    {(result.results || []).map((r) => (
                      <tr key={r.activityId} className="border-t border-border">
                        <td className="px-2 py-1 font-mono text-[10px] text-muted-foreground">{r.activityId.slice(0, 8)}</td>
                        <td className="px-2 py-1 text-muted-foreground">{r.skipped ? '—' : pct(r.before)}</td>
                        <td className="px-2 py-1">{!r.skipped && <ArrowRight className="w-3 h-3 text-muted-foreground" />}</td>
                        <td className="px-2 py-1 font-semibold text-foreground">
                          {r.skipped ? <span className="text-amber-400 font-normal">{r.skipped}</span> : pct(r.after)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)} data-testid="pull-all-close">
              {result ? 'Close' : 'Cancel'}
            </Button>
            {!result && (
              <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={run} disabled={running}
                      data-testid="pull-all-confirm">
                {running ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                Pull all
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PullAllButton;
